import React, { useEffect, useMemo, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import moment from 'moment'
import { Tooltip } from 'react-tooltip'
import { KTSVG } from '../../../_metronic/helpers'
import { PageTitle } from '../../../_metronic/layout/core'
import Pagination from '../../../components/pagination/Pagination'
import NoRecordFound from '../../../components/noRecord/NoRecordFound'
import SearchBarComponent from '../../../components/searchBar/SearchBarComponent'
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent'
import ListingPageSkeleton from '../../../components/loadingComponent/ListingPageSkeleton'
import { constraintConfig } from '../../../constraintConfig'
import { selectAuth } from '../../../features/authSlice'
import { useAppSelector } from '../../../hooks'
import {
  useExportCsvMutation,
  useGetLookupsQuery,
  useGetSummaryQuery,
  useGetUsersQuery,
} from '../../../services/ExpenseAnomalyApi'
import AnomalyFilters, { AnomalyFiltersState } from './components/AnomalyFilters'
import MonthlyCell from './components/MonthlyCell'
import ScheduleModal from './components/ScheduleModal'
import SummaryCards from './components/SummaryCards'
import SsRowActions from '../../../components/tableActions/SsRowActions'
import {
  defaultLastMonthsRange,
  ExpenseAnomalyFilter,
  formatMoney,
  formatPct,
  SelectOption,
} from './types'

const TABLE_COLUMNS = [
  { id: 'user', label: 'User' },
  { id: 'manager', label: 'Manager' },
  { id: 'totalSpend', label: 'Total Spend (6M)' },
  { id: 'anomalies', label: 'Anomalies' },
  { id: 'highestAnomaly', label: 'Highest Anomaly' },
  { id: 'change', label: 'Change' },
]

const buildDefaultFilters = (threshold = 20): AnomalyFiltersState => {
  const range = defaultLastMonthsRange(6)
  return {
    managers: [],
    users: [],
    categories: [],
    periodPreset: '6',
    startDate: range.startDate,
    endDate: range.endDate,
    thresholdPercent: threshold,
    showOnlyAnomalies: true,
  }
}

const toApiFilter = (
  f: AnomalyFiltersState,
  page: number,
  pageSize: number,
  search: string
): ExpenseAnomalyFilter => ({
  managerIds: f.managers.length ? f.managers.map((m) => m.value) : null,
  userAccountIds: f.users.length ? f.users.map((u) => u.value) : null,
  categoryIds: f.categories.length ? f.categories.map((c) => c.value) : null,
  startDate: f.startDate || null,
  endDate: f.endDate || null,
  thresholdPercent: f.thresholdPercent,
  showOnlyAnomalies: f.showOnlyAnomalies,
  search: search || null,
  pageNumber: page,
  pageSize,
})

const AnomaliesDetectionPage: React.FC = () => {
  const navigate = useNavigate()
  const { roleID } = useAppSelector(selectAuth)
  const isManager = roleID === constraintConfig.roleID.role4

  const { data: lookupsData } = useGetLookupsQuery()
  const lookups = lookupsData?.result

  const [draftFilters, setDraftFilters] = useState<AnomalyFiltersState>(() => buildDefaultFilters())
  const [appliedFilters, setAppliedFilters] = useState<AnomalyFiltersState>(() => buildDefaultFilters())
  const [thresholdSeeded, setThresholdSeeded] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [showSchedule, setShowSchedule] = useState(false)
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])

  useEffect(() => {
    if (!lookups || thresholdSeeded) return
    if (lookups.defaultThresholdPercent == null) return
    const t = Number(lookups.defaultThresholdPercent)
    setThresholdSeeded(true)
    setDraftFilters((prev) => ({ ...prev, thresholdPercent: t }))
    setAppliedFilters((prev) => ({ ...prev, thresholdPercent: t }))
  }, [lookups, thresholdSeeded])

  const apiFilter = useMemo(
    () => toApiFilter(appliedFilters, page, pageSize, search),
    [appliedFilters, page, pageSize, search]
  )

  const { data: usersData, isFetching: usersLoading } = useGetUsersQuery(apiFilter)
  const { data: summaryData, isFetching: summaryLoading } = useGetSummaryQuery(apiFilter)
  const [exportCsv, { isLoading: exporting }] = useExportCsvMutation()

  const list = usersData?.result
  const summary = summaryData?.result || list?.summary
  const monthKeys = list?.monthKeys || []
  const items = list?.items || []
  const totalRecords = list?.totalRecords || 0
  const pageCount = Math.max(1, Math.ceil(totalRecords / pageSize))

  const managerOptions: SelectOption[] = (lookups?.managers || []).map((m) => ({
    value: m.id,
    label: m.name,
  }))
  const userOptions: SelectOption[] = (lookups?.users || []).map((u) => ({
    value: u.id,
    label: u.name,
  }))
  const categoryOptions: SelectOption[] = (lookups?.categories || []).map((c) => ({
    value: c.id,
    label: c.name,
  }))

  const pageSubtitle = isManager
    ? 'Team Expense Anomalies — unusual spending patterns for your reportees.'
    : 'Identify unusual spending patterns and potential anomalies in user expenses.'

  const handleRunFilters = () => {
    setPage(1)
    setAppliedFilters({ ...draftFilters })
  }

  const handleShowOnlyToggle = (checked: boolean) => {
    const next = { ...draftFilters, showOnlyAnomalies: checked }
    setDraftFilters(next)
    setAppliedFilters(next)
    setPage(1)
  }

  const toggleColumn = (columnId: string) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]
    )
  }

  const isColVisible = (id: string) => !hiddenColumns.includes(id)

  const handleClear = () => {
    const next = buildDefaultFilters(lookups?.defaultThresholdPercent || 20)
    setDraftFilters(next)
    setAppliedFilters(next)
    setSearch('')
    setPage(1)
  }

  const handleExport = async () => {
    try {
      const blob = await exportCsv(toApiFilter(appliedFilters, 1, 10000, search)).unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `expense-anomalies-${moment().format('YYYYMMDD')}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to export CSV')
    }
  }

  const goDetail = (userAccountId: number) => {
    const qs = new URLSearchParams()
    if (appliedFilters.startDate) qs.set('startDate', appliedFilters.startDate)
    if (appliedFilters.endDate) qs.set('endDate', appliedFilters.endDate)
    if (appliedFilters.thresholdPercent != null)
      qs.set('thresholdPercent', String(appliedFilters.thresholdPercent))
    qs.set('showOnlyAnomalies', String(appliedFilters.showOnlyAnomalies))
    navigate(`/analytics/anomalies/users/${userAccountId}?${qs.toString()}`)
  }

  return (
    <div className='ss-anomalies-page'>
      {/* <PageTitle breadcrumbs={[]}>Anomalies Detection</PageTitle> */}

      <div className='card ss-anomalies-form-card mb-5'>
        <div className='card-body ss-anomalies-form-body pt-5 px-4 px-lg-6 pb-5'>
          <Tooltip id='anomaly-page-info' place='top' />

          <div className='ss-anomalies-report-header d-flex flex-wrap justify-content-between align-items-start gap-3 pb-4 mb-4'>
            <div className='min-w-0'>
              <div className='d-flex align-items-center gap-2 mb-2'>
                  {/* <h1 className='ss-anomalies-report-header__title mb-0'>Anomalies Detection</h1> */}
                <PageTitle breadcrumbs={[]}>Anomalies Detection</PageTitle>
              </div>
              <p className='ss-anomalies-report-header__subtitle mb-0'>{pageSubtitle}                 <span
                  className='ss-anomalies-report-header__info'
                  data-tooltip-id='anomaly-page-info'
                  data-tooltip-content={pageSubtitle}
                  aria-label='About this report'
                >
                  <KTSVG path='/media/icons/duotune/general/gen045.svg' className='svg-icon-5' />
                </span>
</p>
            </div>

            <div className='d-flex flex-wrap align-items-center gap-2 ss-anomalies-report-header__actions'>
              <button
                type='button'
                className='btn btn-sm btn-light ss-anomalies-header-btn'
                onClick={() => setShowSchedule(true)}
              >
                <i className='fa fa-calendar-alt' aria-hidden='true' />
                Schedule Report
              </button>
              <Dropdown align='end'>
                <Dropdown.Toggle
                  variant='light'
                  size='sm'
                  className='ss-anomalies-header-btn ss-anomalies-export-toggle'
                  disabled={exporting}
                >
                  <i className='fa fa-download' aria-hidden='true' />
                  {exporting ? 'Exporting…' : 'Export'}
                </Dropdown.Toggle>
                <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                  <Dropdown.Item onClick={handleExport}>Export CSV</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <AnomalyFilters
            embedded
            value={draftFilters}
            onChange={setDraftFilters}
            managerOptions={managerOptions}
            userOptions={userOptions}
            categoryOptions={categoryOptions}
            hideManager={isManager}
            onClear={handleClear}
            onRun={handleRunFilters}
            running={usersLoading}
          />

          <SummaryCards summary={summary} loading={summaryLoading && !summary} />

          <div className='ss-anomalies-results-section pt-2'>
            <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4'>
              <div className='min-w-0'>
                <h2 className='ss-anomalies-card__title mb-1'>Users with Anomalies</h2>
                <p className='ss-anomalies-card__subtitle mb-0'>
                  Users who have one or more categories exceeding the anomaly threshold (
                  {appliedFilters.thresholdPercent}%) compared to the previous month.
                </p>
              </div>
              <div className='d-flex flex-wrap align-items-center justify-content-lg-end gap-3 ss-anomalies-table-toolbar ms-lg-auto'>
                <div className='form-check form-switch form-check-custom form-check-solid ss-anomalies-show-only'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='anomalyShowOnlyTable'
                    checked={appliedFilters.showOnlyAnomalies}
                    onChange={(e) => handleShowOnlyToggle(e.target.checked)}
                  />
                  <label
                    className='form-check-label ss-anomalies-show-only__label'
                    htmlFor='anomalyShowOnlyTable'
                  >
                    Show Only Anomalies
                  </label>
                </div>
                <SearchBarComponent
                  value={search}
                  placeholder='Search user or manager...'
                  mainClassName='ss-table-search--sm my-0'
                  className='w-225px'
                  onChange={(e) => {
                    setPage(1)
                    setSearch(e.target.value)
                  }}
                />
                <Dropdown align='end' autoClose='outside'>
                  <Dropdown.Toggle
                    variant='light'
                    size='sm'
                    className='ss-anomalies-columns-btn'
                    id='anomaly-columns-dropdown'
                  >
                    <i className='fa fa-cog' aria-hidden='true' />
                    Columns
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    className='ss-anomalies-columns-menu'
                    popperConfig={{ strategy: 'fixed' }}
                    renderOnMount
                  >
                    <div className='px-4 py-2 fw-semibold text-gray-700 fs-7'>Columns</div>
                    <Dropdown.Divider className='my-1' />
                    {TABLE_COLUMNS.map((col) => (
                      <label
                        key={col.id}
                        className='dropdown-item d-flex align-items-center gap-2 mb-0 cursor-pointer'
                      >
                        <input
                          type='checkbox'
                          className='form-check-input m-0'
                          checked={isColVisible(col.id)}
                          onChange={() => toggleColumn(col.id)}
                        />
                        <span>{col.label}</span>
                      </label>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            <div className='ss-anomalies-table-wrap'>
              {usersLoading && !items.length ? (
                <>
                  <div className='d-lg-none ss-listing-skeleton-wrap'>
                    <ListingPageSkeleton rows={10} showPagination />
                  </div>
                  <div className='d-none d-lg-flex justify-content-center py-10'>
                    <LoadingComponent />
                  </div>
                </>
              ) : !items.length ? (
                <NoRecordFound />
              ) : (
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 tabular-invoices-table ss-anomalies-table ss-row-actions-table'>
                    <thead>
                      <tr className='text-uppercase'>
                        {isColVisible('user') && <th className='min-w-175px'>User</th>}
                        {isColVisible('manager') && <th className='min-w-125px'>Manager</th>}
                        {isColVisible('totalSpend') && (
                          <th className='text-end min-w-100px'>Total Spend (6M)</th>
                        )}
                        {isColVisible('anomalies') && (
                          <th className='text-center min-w-80px'>Anomalies</th>
                        )}
                        {monthKeys.map((mk) => (
                          <th key={mk} className='text-end text-nowrap min-w-100px'>
                            {mk}
                          </th>
                        ))}
                        {isColVisible('highestAnomaly') && (
                          <th className='min-w-150px'>Highest Anomaly</th>
                        )}
                        {isColVisible('change') && <th className='text-end min-w-90px'>Change</th>}
                        <th className='text-center min-w-70px sticky-table-actions ss-user-list-actions-col'>
                          <div className='d-flex align-items-center justify-content-center ss-row-actions-header'>
                            <span className='d-none d-xl-inline'>Actions</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row) => (
                        <tr key={row.userAccountId}>
                          {isColVisible('user') && (
                            <td>
                              <button
                                type='button'
                                className='btn btn-link p-0 text-start ss-anomalies-user-link'
                                onClick={() => goDetail(row.userAccountId)}
                              >
                                <span className='ss-anomalies-user-link__name'>{row.userName}</span>
                                <span className='ss-anomalies-user-link__id'>
                                  ({row.userAccountId})
                                </span>
                              </button>
                            </td>
                          )}
                          {isColVisible('manager') && (
                            <td className='ss-anomalies-table__text'>{row.managerName || '—'}</td>
                          )}
                          {isColVisible('totalSpend') && (
                            <td className='text-end ss-anomalies-table__text'>
                              {formatMoney(row.totalSpend)}
                            </td>
                          )}
                          {isColVisible('anomalies') && (
                            <td className='text-center'>
                              <span className='ss-anomalies-count'>{row.anomaliesCount}</span>
                            </td>
                          )}
                          {monthKeys.map((mk) => {
                            const cell = row.monthlySpend?.find((m) => m.monthKey === mk)
                            return (
                              <td key={mk} className='text-end ss-anomalies-month-cell'>
                                <MonthlyCell cell={cell} />
                              </td>
                            )
                          })}
                          {isColVisible('highestAnomaly') && (
                            <td className='ss-anomalies-table__text'>
                              {row.highestAnomalyCategory || '—'}
                            </td>
                          )}
                          {isColVisible('change') && (
                            <td className='text-end ss-anomalies-change'>
                              {formatPct(row.highestAnomalyChangePercent) || '—'}
                            </td>
                          )}
                          <td className='text-center sticky-table-actions ss-user-list-actions-col' onClick={(e) => e.stopPropagation()}>
                            <SsRowActions
                              menuId={`anomaly-actions-${row.userAccountId}`}
                              actions={[
                                {
                                  key: 'view-detail',
                                  label: 'View detail',
                                  icon: <i className='fa fa-eye fs-6 text-primary' aria-hidden='true' />,
                                  onClick: () => goDetail(row.userAccountId),
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {totalRecords > 0 && (
                <Pagination
                  totalResult={totalRecords}
                  toResult={Math.min(page * pageSize, totalRecords)}
                  ofResult={(page - 1) * pageSize + (items.length ? 1 : 0)}
                  pageSize={pageSize}
                  onChange={(e: any) => {
                    setPage(1)
                    setPageSize(Number(e.target.value))
                  }}
                  pageCount={pageCount}
                  onPageChange={({ selected }: { selected: number }) => setPage(selected + 1)}
                  currentPage={page}
                  isLoading={usersLoading}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <ScheduleModal show={showSchedule} onHide={() => setShowSchedule(false)} />
    </div>
  )
}

export default AnomaliesDetectionPage
